import {
  DiscountTierDto,
  ExperienceDto,
  ExperienceListDto,
  VariationDto,
} from "@intelligems/ig-types";

import {
  GenerateScriptInput,
  ScriptAllClasses,
  ScriptCampaign,
  ScriptCampaignInput,
  ScriptCampaignName,
  ScriptClassesUsed,
} from "../../types/checkoutScriptGenerator";

import Common from "./scripts/common";
import LineItemScript from "./scripts/lineItem";
import PaymentScript from "./scripts/payment";
import ShippingScript from "./scripts/shipping";
import { getDiscountInput } from "./utils";

export class CheckoutScriptGenerator {
  static IL = 0;

  static generateCampaignsOutput(
    input: GenerateScriptInput,
    includeClasses: boolean
  ) {
    {
      let defaultCode: string | null = null;
      const classesUsed: ScriptClassesUsed = [];
      const allClasses: ScriptAllClasses = {};
      // Reset the indent level
      CheckoutScriptGenerator.IL = 0;

      switch (input.scriptType) {
        case "line_item":
          Object.assign(allClasses, Common.classes, LineItemScript.classes);
          defaultCode = LineItemScript.defaultCode;
          break;
        case "shipping":
          Object.assign(allClasses, Common.classes, ShippingScript.classes);
          defaultCode = ShippingScript.defaultCode;
          break;
        case "payment":
          Object.assign(allClasses, Common.classes, PaymentScript.classes);
          defaultCode = PaymentScript.defaultCode;
          break;
        default:
          throw Error("Invalid script type");
      }

      // Generate the campaign initialization code (also finds out what classes are used)
      let _campaigns = input.campaigns
        .filter(
          (campaign) =>
            campaign.active === true || campaign.active === undefined
        )
        .map((campaign) => {
          this.IL++;
          const code = this.generateCode(campaign, classesUsed, allClasses);
          this.IL--;
          return code;
        })
        .join(",\n");
      // remove the last `,` from the experiences string (raises syntax error)
      _campaigns = _campaigns.substring(_campaigns.length, 0);
      const campaigns = `## EACH TIME - COPY FROM HERE .... \n\n${_campaigns}\n\n## .... TO HERE`;

      if (!includeClasses) {
        return campaigns;
      }

      let output = generateClassCode(allClasses, classesUsed);
      // Remove first newline
      output = output.substring(1);

      // Replace the default code with the campaign initialization code
      output += defaultCode
        .replace("|", campaigns)
        .replaceAll("CAMPAIGNS", "IG_CAMPAIGNS");
      return output;

      function generateClassCode(
        allClasses: ScriptAllClasses,
        classesUsed: ScriptClassesUsed
      ) {
        let code = "";
        classesUsed.forEach((className) => {
          if (allClasses[className]) {
            code += allClasses[className] + "\n";
          } else {
            throw Error(`Missing class ${className}`);
          }
        });
        return code;
      }
    }
  }

  static generateCode(
    campaign: ScriptCampaign,
    classesUsed: ScriptClassesUsed,
    allClasses: ScriptAllClasses
  ): string | undefined {
    if (campaign.skip) {
      return;
    }

    const INDENT: Record<number, string> = {
      1: "  ",
      2: "    ",
      3: "      ",
      4: "        ",
      5: "          ",
    };

    addUsedClass(campaign.name!, allClasses);
    if (campaign.dependants) {
      campaign.dependants.forEach((dependant) =>
        addUsedClass(dependant, allClasses)
      );
    }

    const inputsCode = (campaign.inputs || [])
      .map((input, index) => {
        if (typeof input === "object" && input.inputs) {
          this.IL++;
          const code = this.generateCode(input, classesUsed, allClasses);
          this.IL--;
          return code;
        } else if (typeof input === "object" && input.name !== "none") {
          addUsedClass(input.name, allClasses);
          return `${INDENT[this.IL + 1]}${input.name}.new()`;
        } else if (typeof input === "object" && input.name === "none") {
          return `${INDENT[this.IL + 1]}nil`;
        } else {
          return `${INDENT[this.IL + 1]}${input as string}`;
        }
      })
      .join(",\n");

    return `\
${INDENT[this.IL]}${campaign.name}.new(
${inputsCode}
${INDENT[this.IL]})`;

    function addUsedClass(className: string, allClasses: ScriptAllClasses) {
      // Grab what inherits from the class (if anything)
      let inheritsFrom;

      if (allClasses[className]) {
        inheritsFrom = allClasses[className].split("\n")[1];
      } else {
        throw Error(`Missing class ${className}`);
      }

      if (inheritsFrom.indexOf("<") > -1) {
        inheritsFrom = inheritsFrom.split("<")[1].trim();
        if (classesUsed.indexOf(inheritsFrom) === -1) {
          classesUsed.push(inheritsFrom);
        }
      }

      if (classesUsed.indexOf(className) === -1) {
        classesUsed.push(className);
      }
    }
  }

  static removeRunIgCampaigns(output: string): string {
    return output.replace(
      `IG_CAMPAIGNS.each do |campaign|
  campaign.run_with_hooks(Input.cart)
end`,
      ""
    );
  }

  static removeOutputCart(output: string): string {
    return output.replace("Output.cart = Input.cart", "");
  }

  static addOutputCart(output: string): string {
    return (
      output +
      (output.endsWith("\n") ? "" : "\n") +
      "\nOutput.cart = Input.cart"
    );
  }

  static addHeaderComment(output: string, comment?: string): string {
    const _comment = comment || "# INTELLIGEMS DISCOUNTING \n";
    return _comment + output;
  }

  static addPriceTestingAndGWP(output: string): string {
    const _staticPriceTestingSnippet = `
# BEGIN -- INTELLIGEMS PRICE TESTING (AND GWP)
class Intelligems
  def initialize(discount_property = '_igp', allow_free = false)
    @volume_discount_property = '_igvd'
    @volume_discount_message_property = '_igvd_message'
    @deprecated_property = '_igLineItemDiscount'
    @discount_property = discount_property
    @allow_free = allow_free
    @gwp_property = '_igGWP'
  end

  def discount_product(line_item)
    ig_price = Money.new(cents: line_item.properties[@discount_property])

    discount = line_item.line_price - (ig_price * line_item.quantity)
    if discount > Money.zero
      if @allow_free or discount < line_item.line_price
        line_item.change_line_price(line_item.line_price - discount, message: 'Discount')
      end
    end

  end

  def deprecated_discount_product(line_item)
    discount = Money.new(cents: line_item.properties[@deprecated_property])
    discount *= line_item.quantity

    if @allow_free or discount < line_item.line_price
      line_item.change_line_price(line_item.line_price - discount, message: 'Displayed Price')
    end
  end

  def volume_discount(line_item)
    discount = Money.new(cents: line_item.properties[@volume_discount_property])
    discount *= line_item.quantity

    if discount < line_item.line_price
      message = line_item.properties[@volume_discount_message_property]
      line_item.change_line_price(line_item.line_price - discount, message: message)
    end
  end
  
  def gwp_product(line_item)
    discount = Money.new(cents: line_item.line_price.cents / line_item.quantity)
    line_item.change_line_price(line_item.line_price - discount, message: 'Free Gift')
  end

  def run(cart)
    cart.line_items.each do |line_item|
      puts line_item.variant.id
      if !line_item.properties[@discount_property].nil? && !line_item.properties[@discount_property].empty?
        discount_product(line_item)
      elsif !line_item.properties[@gwp_property].nil? && !line_item.properties[@gwp_property].empty?
        gwp_product(line_item)
      elsif !line_item.properties[@volume_discount_property].nil? && !line_item.properties[@volume_discount_property].empty?
        volume_discount(line_item)
      elsif !line_item.properties[@deprecated_property].nil? && !line_item.properties[@deprecated_property].empty?
        deprecated_discount_product(line_item)
      end
    end
  end
end

intelligems = Intelligems.new()
intelligems.run(Input.cart)
# END - INTELLIGEMS PRICE TESTING (AND GWP)

`;
    return _staticPriceTestingSnippet + output;
  }

  static addRejectCodeComment(output: string, comment?: string): string {
    const _comment =
      comment ||
      "# Remove 'true' (last argument) from an item in IG_CAMPAIGNS to allow code stacking for that campaign \n";
    return output + _comment;
  }

  static generateSimpleScript(input: GenerateScriptInput, fullScript: boolean) {
    let output = this.generateCampaignsOutput(input, fullScript);
    if (!fullScript) {
      return output;
    }
    //output = this.addRejectCodeComment(output);
    output = this.removeOutputCart(output);
    output = this.removeRunIgCampaigns(output);
    output = this.addCampaignLookupLoop(output);
    output = this.addCampaignRunLoop(output);
    output = this.addHeaderComment(output);
    output = this.addPriceTestingAndGWP(output);
    output = this.addOutputCart(output);
    return output;
  }

  static addVariationIfStatement(output: string, variations: VariationDto[]) {
    variations.forEach((tg, index) => {
      output += `testGroup${index + 1} = '${tg.id}' # ${tg.name}${
        tg.isControl ? " (Control Group)" : ""
      }\n`;
    });

    output += "\n";
    variations.forEach((tg, index) => {
      output += `${index === 0 ? "if" : "elsif"} testGroup == testGroup${
        index + 1
      }
  [IG_CAMPAIGNS[${index}]].each do |campaign|
    campaign.run_with_hooks(Input.cart)
  end\n`;
    });

    if (variations.length > 0) {
      output += "end";
    }

    return output;
  }

  static shortId(uuid?: string): string | undefined {
    return uuid?.slice(-12);
  }

  // static addCampaignIfStatement(output: string, variations: VariationDto[]) {
  //   variations.forEach((tg, index) => {
  //     output += `campaign${index + 1} = '${this.shortId(
  //       tg.testExperienceId
  //     )}' # ${tg.testCampaign?.name}${tg.isControl ? " (Control)" : ""}\n`;
  //   });
  //
  //   output += "\n";
  //   variations.forEach((tg, index) => {
  //     output += `${index === 0 ? "if" : "elsif"} campaigns.include campaign${
  //       index + 1
  //     }
  // [IG_CAMPAIGNS[${index}]].each do |campaign|
  //   campaign.run_with_hooks(Input.cart)
  // end\n`;
  //   });
  //
  //   if (variations.length > 0) {
  //     output += "end";
  //   }
  //
  //   return output;
  // }

  static addCampaignLookupLoop(output: string): string {
    return (
      output +
      `\ncampaignsProperty='_igCampaigns'
campaigns = []

Input.cart.line_items.each do |line_item|
  if !line_item.properties[campaignsProperty].nil? && ! line_item.properties[campaignsProperty].empty?
    propertyVal = line_item.properties[campaignsProperty]
    propertyList = propertyVal.split(',')
    propertyList.each do |campaign|
      if !campaigns.include? campaign
        campaigns.push(campaign)
      end
    end
  end
end\n\n`
    );
  }

  static addCampaignRunLoop(output: string): string {
    return (
      output +
      `\ncode = Input.cart.discount_code
reject = false
linkCampaign = nil
minCart = Float::INFINITY

IG_CAMPAIGNS.each do |campaign|
  if !campaigns.nil? && (campaigns.include? campaign.campaign_id) 
    if (campaign.should_stack)
      campaign.run_with_hooks(Input.cart)
      if (campaign.reject_code && code && campaign.discount_applied)
        reject = true
      end
    else
      cartTotal = campaign.getCartTotal(Input.cart)
      if (cartTotal.cents < minCart)
        minCart = cartTotal.cents
        linkCampaign = campaign
      end
    end
  end
end

if linkCampaign
  linkCampaign.run_with_hooks(Input.cart)
end

if reject || (!linkCampaign.nil? && linkCampaign.reject_code && code && linkCampaign.discount_applied)
  Input.cart.discount_code.reject(
    message: "Discount codes cannot be redeemed with automatic discounts!"
  )
end\n\n`
    );
  }

  static buildDiscountTiers(tiers: DiscountTierDto[]) {
    const tierInput: string[] = [];

    for (const tier of tiers.filter(
      (tier) => tier.minimumUnits !== undefined // && tier.minimumUnits !== 0
    )) {
      tierInput.push(
        `{:tier => "${tier.minimumUnits}", :discount => "${
          tier.unitDiscount
        }", :message => "${tier.discountTitle || "Bundle and Save"}"}`
      );
    }

    return "[" + tierInput.join(",\t") + "]";
  }

  static buildCampaigns(
    igCampaigns: ExperienceDto[],
    controlProductIds?: string[]
  ): GenerateScriptInput {
    const campaigns: ScriptCampaign[] = igCampaigns
      .filter(
        (igCampaign) => !igCampaign.isDeleted && igCampaign.variations[0].offer
      )
      .map((igCampaign) => {
        const discount = igCampaign.variations[0].offer!;
        const productIds: string[] =
          controlProductIds ||
          igCampaign.offerProducts?.map((testProduct) => testProduct.id);

        const productTagSelector: {
          name: ScriptCampaignName;
          inputs?: string[];
        } = {
          name: "ProductTagSelector",
          inputs: [
            ":does_not",
            ":include",
            JSON.stringify(["ig-campaign-exclude"]),
          ],
        };

        const productInput: ScriptCampaignInput =
          productIds?.length === 0
            ? productTagSelector
            : {
                name: "AndSelector",
                inputs: [
                  {
                    name: "ProductIdSelector",
                    inputs: [":is_one", JSON.stringify(productIds)],
                  },
                  productTagSelector,
                ],
              };

        const discountInput: ScriptCampaignInput = getDiscountInput(discount);

        const totalType =
          discount.unitType === "unit"
            ? ":discountable_total_items"
            : ":discountable_total";

        const campaign: ScriptCampaign = {
          name: "TieredDiscount",
          label: igCampaign.name,
          active: true,
          id: igCampaign.id,
          dependants: [
            "PercentageDiscount",
            "FixedTotalDiscount",
            "FixedItemDiscount",
          ],
          inputs: [
            ":all",
            { name: "none" },
            { name: "none" },
            productInput,
            discountInput,
            totalType,
            this.buildDiscountTiers(discount.tiers),
            `"${this.shortId(igCampaign.id)}"`,
            discount.combinesWithIntelligems,
            discount.shouldRejectCodes,
          ],
        };
        return campaign;
      });

    return {
      scriptType: "line_item",
      campaigns,
    };
  }
}
